// Kenney (CC0) assets shipped with the site under public/assets/kenney: image textures for level
// pieces, scenery models and some sounds. Loaded on demand; the manifest lists what's there.

import * as THREE from 'three';

export interface AssetManifest {
  textures: { id: string; group: string; file: string; label?: string }[];
  models: { id: string; group: string; file: string }[];
  sounds: Record<string, string[]>;
}

export const KENNEY_BASE = `${import.meta.env.BASE_URL ?? '/'}assets/kenney/`;

let manifest: Promise<AssetManifest> | null = null;
let loaded: AssetManifest | null = null;

export function assetManifest(): Promise<AssetManifest> {
  manifest ??= fetch(KENNEY_BASE + 'manifest.json')
    .then((r) => {
      if (!r.ok) throw new Error('manifest ' + r.status);
      return r.json() as Promise<AssetManifest>;
    })
    .then((m) => (loaded = m))
    .catch(() => (loaded = { textures: [], models: [], sounds: {} }));
  return manifest;
}

/** The manifest if it has loaded already. */
export function manifestNow(): AssetManifest | null {
  return loaded;
}

const textureCache = new Map<string, Promise<THREE.Texture | null>>();

/** Image texture by manifest id ("proto-dark-01"), repeating, sRGB. */
export function imageTexture(id: string, anisotropy: number): Promise<THREE.Texture | null> {
  let p = textureCache.get(id);
  if (!p) {
    p = assetManifest().then(async (m) => {
      const t = m.textures.find((x) => x.id === id);
      if (!t) return null;
      try {
        const tex = await new THREE.TextureLoader().loadAsync(KENNEY_BASE + t.file);
        tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
        tex.colorSpace = THREE.SRGBColorSpace;
        tex.anisotropy = anisotropy;
        return tex;
      } catch {
        return null;
      }
    });
    textureCache.set(id, p);
  }
  return p;
}

const modelCache = new Map<string, Promise<THREE.Object3D | null>>();

/** A scenery model by manifest id; clone the result for each use. */
export function sceneryModel(id: string): Promise<THREE.Object3D | null> {
  let p = modelCache.get(id);
  if (!p) {
    p = assetManifest().then(async (m) => {
      const e = m.models.find((x) => x.id === id);
      if (!e) return null;
      try {
        const { GLTFLoader } = await import('three/addons/loaders/GLTFLoader.js');
        const gltf = await new GLTFLoader().loadAsync(KENNEY_BASE + e.file);
        return gltf.scene;
      } catch {
        return null;
      }
    });
    modelCache.set(id, p);
  }
  return p;
}
